import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import type { DashboardLayout } from '@pages/IntroducingBroker/DashboardPoC/types'

import type { IconButtonProps } from '@ui/components/Button/IconButton'
import { ListItem } from '@ui/components/List'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { EditIcon } from '@ui/icons/Edit.icon'
import { StarIcon } from '@ui/icons/Star.icon'
import { StarFilledIcon } from '@ui/icons/StarFilled.icon'
import { TrashIcon } from '@ui/icons/Trash.icon'
import type { IconRendererType } from '@ui/icons/utils/IconRenderer'

import type { LayoutSelectorProps } from './LayoutSelector'

interface LayoutItemProps {
  index: number
  layout: DashboardLayout
  currentLayoutId: string
  defaultLayoutId: string
  handleSetDefaultLayout: (layoutId: string, isDefault?: boolean) => Promise<void>
  onSelect: (layoutId: string) => void
  onEditMode: (id: string) => void
  onDeleteLayout: (layoutId: string) => void | Promise<void>
  openLayoutActionModal: LayoutSelectorProps['openLayoutActionModal']
}

export const LayoutItem: FC<LayoutItemProps> = ({
  index,
  layout,
  currentLayoutId,
  onSelect,
  onEditMode,
  onDeleteLayout,
  openLayoutActionModal,
  defaultLayoutId,
  handleSetDefaultLayout,
}) => {
  const { t } = useTranslation()
  const widgetsText = t('Dashboard.WidgetsCount', { count: layout.widgets.length })
  const subtitle = [layout.description, `(${widgetsText})`].filter(Boolean).join(' ')
  const isDefault = layout.id === defaultLayoutId
  const actionButtons: IconButtonProps[] = [
    {
      'aria-label': t('Delete'),
      icon: { icon: TrashIcon, color: 'text-med-em' },
      size: 'sm',
      variant: 'ghost',
      onClick: (event) => {
        event.stopPropagation()
        openLayoutActionModal({
          actionEvent: 'delete',
          onAccept: () => onDeleteLayout(layout.id),
        })
      },
      'data-action': 'delete-layout',
    },
    {
      'aria-label': t('Edit'),
      icon: { icon: EditIcon, color: 'text-med-em' },
      size: 'sm',
      variant: 'ghost',
      onClick: (event) => {
        event.stopPropagation()
        onEditMode(layout.id)
      },
      'data-action': 'edit-layout',
    },
    {
      'aria-label': t('Dashboard.Make this layout default'),
      icon: getLayoutIcon(isDefault),
      size: 'sm',
      variant: 'ghost',
      onClick: (event) => {
        event.stopPropagation()
        void handleSetDefaultLayout(layout.id, !isDefault)
      },
      'data-action': 'star-layout',
    },
  ]

  return (
    <ListItem
      title={layout.name}
      subtitle={subtitle}
      onClick={() => onSelect(layout.id)}
      startSlot={{
        shape: 'squircle',
        size: 'x32',
        label: String(index + 1),
        bg: 'neutral-inverse-white-alpha-6',
        pimple:
          layout.id === currentLayoutId
            ? {
                size: 'x16',
                bg: 'surface-secondary-high-em',
                icon: { icon: CircleCheckIcon, color: 'text-inverse-black-neutral' },
              }
            : undefined,
      }}
      endSlot={{ iconButtons: actionButtons }}
    />
  )
}

export function getLayoutIcon(isActive: boolean): IconRendererType {
  return isActive
    ? { icon: StarFilledIcon, color: 'text-warning-med-em' }
    : { icon: StarIcon, color: 'text-med-em' }
}
